import { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import DialogActions from "components/Actions/dialogs/components/DialogActions";
import { EditInlineDialogProps } from "components/Actions/dialogs/types/interfaces";
import { DEVICE_MODEL_ACTIONS } from "components/Actions/types/enums";
import Autocomplete from "components/DesignComponents/Autocomplete";
import Checkbox from "components/DesignComponents/Checkbox";
import Dialog from "components/DesignComponents/Dialog";
import InputField, {
    INPUT_FIELD_SIZES
} from "components/DesignComponents/InputField";
import { useApi } from "shared/hooks/useApi";
import { useMediaQueries } from "shared/hooks/useMediaQueries";
import {
    DROPDOWN_SIZES,
    ENDPOINTS,
    FILTER_NAMES,
    PLATFORMS
} from "shared/types/enums";

const EditModelInline = ({ data, isOpen, close }: EditInlineDialogProps) => {
    const { t } = useTranslation();
    const { toMd } = useMediaQueries();
    const { updateData, handleResponse } = useApi();

    const [isLoading, setIsLoading] = useState(false);

    const [value, setValue] = useState({
        [FILTER_NAMES.Platform]: null,
        [FILTER_NAMES.WebEditorSupport]: null
    });

    const { platform, web_editor_support } = value;

    useEffect(() => {
        if (data) {
            const { platform, web_editor_support } = data;

            setValue({
                [FILTER_NAMES.Platform]: platform,
                [FILTER_NAMES.WebEditorSupport]: web_editor_support
            });
        }
    }, [data]);

    const platforms = Object.values(PLATFORMS).filter(
        platform => platform !== PLATFORMS.Unknown
    );

    const handleValueChange = (
        filterName: FILTER_NAMES.Platform | FILTER_NAMES.WebEditorSupport,
        newValue: string | boolean
    ) => setValue({ ...value, [filterName]: newValue });

    const handleSubmit = async () => {
        const payload = {
            [FILTER_NAMES.Platform]: platform,
            [FILTER_NAMES.WebEditorSupport]: web_editor_support
        };

        try {
            setIsLoading(true);

            const response = await updateData(
                ENDPOINTS.DeviceModels,
                data.id,
                payload
            );

            close();
            handleResponse(response);
        } catch (error) {
            handleResponse(error);
        }

        setIsLoading(false);
    };

    return (
        <Dialog
            title={t(`General##${DEVICE_MODEL_ACTIONS.EditModel}`)}
            isTitleSeparator
            isActionsSeparator
            open={isOpen}
            close={close}
            submit={handleSubmit}
            slotProps={{
                transition: {
                    onExited: () => {
                        setValue({
                            [FILTER_NAMES.Platform]: null,
                            [FILTER_NAMES.WebEditorSupport]: null
                        });

                        setIsLoading(false);
                    }
                }
            }}
            actions={
                <DialogActions
                    onClose={close}
                    isLoading={isLoading}
                    isReadyToConfirm={platform !== null}
                />
            }
            extendToMaxWidth={toMd}
        >
            <InputField
                size={INPUT_FIELD_SIZES.Medium}
                fullWidth
                readOnly
                value={data?.name || ""}
                labelLeft={t("Table##model")}
            />

            <Autocomplete
                fullWidth
                isRequired
                disableClearable
                labelLeft={t("Table##platform")}
                size={DROPDOWN_SIZES.Medium}
                options={platforms}
                value={platform}
                onChange={(_, val) =>
                    handleValueChange(FILTER_NAMES.Platform, val)
                }
                readOnly={isLoading}
                textFieldParams={{
                    placeholder: t("Dialog##select")
                }}
                customStyle={{ margin: "16px 0" }}
            />

            <Checkbox
                label={t("Table##tct web support")}
                checked={web_editor_support ?? false}
                onChange={(_, isChecked) =>
                    handleValueChange(FILTER_NAMES.WebEditorSupport, isChecked)
                }
                disabled={isLoading}
            />
        </Dialog>
    );
};

export default EditModelInline;
